import * as React from "react";
import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  Divider,
  Flex,
  Footer,
  Heading,
  Text,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import styles from "./resolve_message_options_dialog.module.css";

export interface ResolveMessageOptionsVisibility {
  reply: boolean;
  replyAll: boolean;
  forward: boolean;
  comment: boolean;
  move: boolean;
}

export interface ResolveMessageOptionsDialogProps {
  onClose: () => void;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onComment: () => void;
  onMoveToContext: () => void;
  visibility: ResolveMessageOptionsVisibility;
}

export function ResolveMessageOptionsDialog({
  onClose,
  onReply,
  onReplyAll,
  onForward,
  onComment,
  onMoveToContext,
  visibility,
}: Readonly<ResolveMessageOptionsDialogProps>) {
  const { t } = useTranslation();

  const hasAny =
    visibility.reply ||
    visibility.replyAll ||
    visibility.forward ||
    visibility.comment ||
    visibility.move;

  const wrap = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return (
    <Dialog>
      <Heading UNSAFE_className={styles.heading}>
        {t("resolve_message", {
          ns: "layout_components",
          defaultValue: "Resolve message",
        })}
      </Heading>
      <Content>
        <Text UNSAFE_className={styles.hint}>
          {t("resolve_message_hint", {
            ns: "layout_components",
            defaultValue: "Choose how you want to resolve this message.",
          })}
        </Text>
        <Flex
          direction="column"
          gap="size-100"
          marginTop="size-200"
          UNSAFE_className={styles.actions}
        >
          {visibility.reply ? (
            <Button
              variant="secondary"
              UNSAFE_className={clsx(styles.action_btn)}
              onPress={wrap(onReply)}
            >
              {t("reply", { ns: "layout_components", defaultValue: "Reply" })}
            </Button>
          ) : null}
          {visibility.replyAll ? (
            <Button
              variant="secondary"
              UNSAFE_className={clsx(styles.action_btn)}
              onPress={wrap(onReplyAll)}
            >
              {t("reply_all", {
                ns: "layout_components",
                defaultValue: "Reply all",
              })}
            </Button>
          ) : null}
          {visibility.forward ? (
            <Button
              variant="secondary"
              UNSAFE_className={clsx(styles.action_btn)}
              onPress={wrap(onForward)}
            >
              {t("forward", {
                ns: "layout_components",
                defaultValue: "Forward",
              })}
            </Button>
          ) : null}
          {visibility.comment ? (
            <Button
              variant="secondary"
              UNSAFE_className={clsx(styles.action_btn)}
              onPress={wrap(onComment)}
            >
              {t("comment", { ns: "layout_components", defaultValue: "Comment" })}
            </Button>
          ) : null}
          {visibility.move ? (
            <>
              <Divider size="S" marginY="size-100" />
              <Button
                variant="secondary"
                UNSAFE_className={clsx(styles.action_btn)}
                onPress={wrap(onMoveToContext)}
              >
                {t("move_to_context", {
                  ns: "layout_components",
                  defaultValue: "Move to context",
                })}
              </Button>
            </>
          ) : null}
          {!hasAny ? (
            <Text UNSAFE_className={styles.empty}>
              {t("resolve_no_options", {
                ns: "layout_components",
                defaultValue: "No resolve actions are available for this message.",
              })}
            </Text>
          ) : null}
        </Flex>
      </Content>
      <Footer>
        <ButtonGroup>
          <Button variant="secondary" onPress={onClose}>
            {t("cancel", {
              ns: "layout_components",
              defaultValue: "Cancel",
            })}
          </Button>
        </ButtonGroup>
      </Footer>
    </Dialog>
  );
}
